/* eslint-disable react-hooks/rules-of-hooks */
import { memo, useContext, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { Formik } from 'formik';
import headerBg from '@knowledge/assets/header-bg.png';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import EditKcArea from './EditKc';
import ValuesArea from './ValuesArea';
import SpuArea from './SpuArea';
import { ConfigSkuItemProvider } from './context';
import { getMultiLangValue } from './helpers';
import { useTranslate } from '@tolgee/react';
import { CurrentKnowledgeValueContext } from './context';
import { get, sortBy } from 'lodash-es';

const ConfigItem = memo(
  (props: {
    knowledge: Knowledge.SingleClass.Knowledge;
    citeMode?: boolean;
    activeKnowledgeId?: number;
    onKnowledgeUpdate?: (knowledge: Knowledge.SingleClass.Knowledge) => void;
  }) => {
    const { knowledge, citeMode, activeKnowledgeId, onKnowledgeUpdate } = props;
    const lang = useSystemLanguageValue();
    const knowledgeName = useMemo(() => {
      return getMultiLangValue(knowledge.nameMultiLang, lang, knowledge.knowledgeName);
    }, [lang, knowledge]);
    const { t } = useTranslate();
    const { active, setActive } = useContext(CurrentKnowledgeValueContext);
    useEffect(() => {
      if (activeKnowledgeId) {
        setActive(activeKnowledgeId);
      } else {
        !active && setActive(get(knowledge, 'values[0].id', ''));
      }
    }, [knowledge, active, setActive, activeKnowledgeId]);

    return (
      <Formik
        key={knowledge.id}
        initialValues={{
          ...knowledge,
          knowledgeName,
        }}
        enableReinitialize
        onSubmit={(values) => {
          if (citeMode && onKnowledgeUpdate) {
            onKnowledgeUpdate(values);
          }
        }}
      >
        {(props) => {
          const { values, setValues } = props;
          const refsCurrent = {
            values,
            setValues,
          };
          const refs = useRef(refsCurrent);
          refs.current = refsCurrent;
          useEffect(() => {
            if (activeKnowledgeId) {
              const { values, setValues } = refs.current;
              setValues({
                ...values,
                values: sortBy(values.values, ({ id, isDefaultValue }) =>
                  `${id}` === `${activeKnowledgeId}` ? 0 : isDefaultValue ? 1 : 2,
                ),
              });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [activeKnowledgeId]);
          return (
            <div className="h-full bg-[var(--fett-bg-panel)] flex flex-col rounded-[6px] border border-solid border-[var(--fett-border-default)]">
              {!citeMode && (
                <Header className="basis-[56px] flex-shrink-0 flex items-center p-[16px]">
                  <div
                    className="flex-shrink-0 max-w-[500px] truncate mr-[16px] text-[var(--fett-text-primary)] font-medium"
                    title={values.knowledgeName}
                  >
                    {values.knowledgeName}
                  </div>
                  {values.valueAsDirectAnswer && (
                    <div className="text-[12px] text-[var(--fett-surface-error-text)]">
                      {t('建议配置具体话术，机器人将使用所配置的话术回答')}
                    </div>
                  )}
                  <EditKcArea />
                </Header>
              )}
              <ConfigSkuItemProvider>
                <div className="flex-grow flex basis-0 overflow-hidden">
                  <ValuesArea citeMode={citeMode} id={values.id} />
                  <SpuArea citeMode={citeMode} knowledgeName={values.knowledgeName} />
                </div>
              </ConfigSkuItemProvider>
            </div>
          );
        }}
      </Formik>
    );
  },
);

ConfigItem.displayName = 'ConfigItem';

const Header = styled.div`
  background: top left / contain no-repeat url(${`"${headerBg}"`});
  border-bottom: 1px solid var(--fett-border-default);
`;

export default ConfigItem;
